import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CategoryKind, type BudgetStatusValue, type BudgetView } from '@financial/shared';
import { useBudget, useUpsertBudget } from '../features/budgets/useBudgets';
import { useCard } from '../features/creditCard/useCreditCard';
import { useCategories } from '../features/categories/useCategories';
import { useInsights } from '../features/insights/useInsights';
import { formatMoney } from '../lib/format';
import { getApiErrorMessage } from '../lib/errors';
import { MonthPicker } from '../components/ui/DatePicker';
import { GuidedPageHeader } from '../components/onboarding/GuidedPageHeader';
import { HelperText, Skeleton } from '../components/ui/Primitives';
import { SkeletonState } from '../components/ui/SkeletonState';
import { getPurchasePolicy, safeExtraSavings } from '../lib/purchaseCapacity';
import { categoryLimitAllocation, toCategoryLimitPayload } from '../lib/categoryLimits';
import { currentYearMonth } from '../lib/businessDate';

const STATUS_META: Record<BudgetStatusValue, { color: string; label: string }> = {
  ok: { color: '#15803d', label: 'Vas bien' },
  warning: { color: '#b45309', label: 'Cerca del límite' },
  over: { color: '#b91c1c', label: 'Excedido' },
};

export function BudgetPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedMonth = searchParams.get('mes');
  const month = /^\d{4}-(0[1-9]|1[0-2])$/.test(requestedMonth ?? '')
    ? requestedMonth!
    : currentYearMonth();
  const budgetQ = useBudget(month);
  const data = budgetQ.data;

  const selectMonth = (nextMonth: string) => {
    const normalizedMonth = nextMonth || currentYearMonth();
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set('mes', normalizedMonth);
        return next;
      },
      { replace: true },
    );
  };

  return (
    <div className="reference-page w-full">
      <GuidedPageHeader
        section="budget"
        title="Presupuesto"
        description="Define un límite para el gasto variable después de tus compromisos y ahorro."
        actions={<MonthPicker value={month} onChange={selectMonth} />}
      />

      {budgetQ.isLoading && <BudgetSkeleton />}
      {data && <BudgetContent month={month} data={data} />}
    </div>
  );
}

export function BudgetSkeleton() {
  return (
    <SkeletonState
      label="Cargando presupuesto…"
      className="grid gap-4 lg:grid-cols-[minmax(300px,.78fr)_minmax(0,1.22fr)]"
    >
      <div className="space-y-4">
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-56 w-full" />
      </div>
      <div className="space-y-4">
        <Skeleton className="h-36 w-full" />
        <Skeleton className="h-72 w-full" />
      </div>
    </SkeletonState>
  );
}

function BudgetContent({ month, data }: { month: string; data: BudgetView }) {
  const status = STATUS_META[data.status];
  const pct = Math.min(100, Math.round(data.usageRatio * 100));

  return (
    <div className="space-y-4 lg:grid lg:grid-cols-[minmax(300px,.78fr)_minmax(0,1.22fr)] lg:items-start lg:gap-4 lg:space-y-0">
      <div className="space-y-4">
        {/* Semáforo gasto vs límite */}
        <section className="ui-card">
          <div className="flex items-baseline justify-between">
            <h2 className="text-sm font-semibold text-slate-700">Gasto vs. límite</h2>
            <span className="text-sm font-semibold" style={{ color: status.color }}>
              {status.label}
            </span>
          </div>
          <p className="mt-1 text-2xl font-bold text-slate-900">
            {formatMoney(data.variableSpent)}{' '}
            <span className="text-sm font-normal text-slate-600">
              de {formatMoney(data.effectiveLimit)}
            </span>
          </p>
          <div className="mt-2 h-3 w-full overflow-hidden rounded-full bg-slate-100">
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${pct}%`, backgroundColor: status.color }}
            />
          </div>
          <div className="mt-2 flex justify-between text-xs text-slate-500">
            <span>{pct}% usado</span>
            <span>
              {data.availableFree < 0
                ? 'Exceso sobre el límite mensual: '
                : 'Restante del límite mensual: '}
              <span className={data.availableFree < 0 ? 'text-expense' : 'text-income'}>
                {formatMoney(Math.abs(data.availableFree))}
              </span>
            </span>
          </div>
          <p className="mt-2 text-xs leading-5 text-slate-500">
            Es el límite de gasto variable menos lo que ya registraste en el mes; no es el saldo de
            tus cuentas ni el estimado de cierre.
          </p>
        </section>

        {/* Límite manual vs sugerido */}
        <LimitForm month={month} data={data} />

        {/* Desglose de compromisos fijos */}
        <section className="ui-card">
          <h2 className="mb-2 text-sm font-semibold text-slate-700">Cómo se calcula tu límite</h2>
          {data.overcommitted && (
            <p className="mb-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-expense">
              Tus compromisos fijos + ahorro superan tus ingresos del mes.
            </p>
          )}
          <dl className="space-y-1 text-sm">
            <Row label="Ingreso seguro para gastar" value={data.breakdown.monthlyIncome} positive />
            {data.breakdown.expectedIncome > data.breakdown.monthlyIncome && (
              <Row
                label="Pronóstico adicional (aún no reservado)"
                value={data.breakdown.expectedIncome - data.breakdown.monthlyIncome}
                positive
              />
            )}
            <Row label="− Mensualidades MSI previas" value={-data.breakdown.cardPaymentProjected} />
            <Row label="− Préstamos y deudas" value={-data.breakdown.loanPayments} />
            <Row label="− Cargos fijos en uso" value={-data.breakdown.recurringExpenses} />
            {data.breakdown.plannedExpenses > 0 && (
              <Row label="− Gastos planeados del mes" value={-data.breakdown.plannedExpenses} />
            )}
            <Row label="− Ahorro objetivo del mes" value={-data.breakdown.savingsTarget} />
            <SavingsTargetDetail data={data} />
            <div className="mt-1 border-t border-slate-100 pt-1">
              <Row label="= Límite sugerido" value={data.suggestedLimit} strong />
            </div>
          </dl>
          <HelperText className="mt-3" tone="info">
            Recibido a la fecha: {formatMoney(data.breakdown.receivedIncome)}. Para no gastar un
            bono antes de tenerlo, el límite usa {formatMoney(data.breakdown.monthlyIncome)}: tu
            ingreso estable ({formatMoney(data.breakdown.stableIncome)}) o lo ya recibido, el mayor.
            El pronóstico completo es {formatMoney(data.breakdown.expectedIncome)}. El gasto de
            arriba incluye compras con débito/efectivo y cargos nuevos de tarjeta; no vuelve a sumar
            el pago de esa tarjeta, ni incluye fijos o traspasos.
          </HelperText>
        </section>
      </div>

      <div className="space-y-4">
        <SpendingSpaceCard month={month} data={data} />
        <CategoryLimitsForm month={month} data={data} />
      </div>
    </div>
  );
}

function SavingsTargetDetail({ data }: { data: BudgetView }) {
  const { breakdown } = data;
  const ratePercent = Math.round(breakdown.targetSavingsRate * 100);
  const missingToTarget = Math.max(0, breakdown.savingsTarget - breakdown.committedSavings);
  const goalComesFromRate = breakdown.savingsTargetFromRate >= breakdown.committedSavings;

  return (
    <div className="my-2 rounded-xl border border-blue-100 bg-blue-50/70 p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-slate-800">¿De dónde sale este ahorro?</p>
          <p className="mt-0.5 text-xs leading-5 text-slate-600">
            El presupuesto usa el mayor entre tu porcentaje configurado y los apartados que ya
            planeaste; no suma ambos dos veces.
          </p>
        </div>
        <Link
          to="/ajustes#ahorro"
          className="tap-target inline-flex shrink-0 items-center text-xs font-semibold text-savings hover:text-blue-700"
        >
          Ajustar porcentaje →
        </Link>
      </div>
      <div className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
        <div className="rounded-lg border border-white/90 bg-white/85 p-2.5">
          <p className="text-xs text-slate-500">Meta configurada</p>
          <p className="mt-0.5 font-semibold text-slate-800">
            {ratePercent}% × {formatMoney(breakdown.monthlyIncome)} ={' '}
            {formatMoney(breakdown.savingsTargetFromRate)}
          </p>
        </div>
        <div className="rounded-lg border border-white/90 bg-white/85 p-2.5">
          <p className="text-xs text-slate-500">Apartados ya comprometidos</p>
          <p className="mt-0.5 font-semibold text-savings">
            {formatMoney(breakdown.committedSavings)}
            {breakdown.savedSoFar > 0 && (
              <span className="font-normal text-slate-500">
                {' '}
                · {formatMoney(breakdown.savedSoFar)} ya enviados
              </span>
            )}
          </p>
        </div>
      </div>
      <p className="mt-3 text-xs leading-5 text-slate-600">
        {goalComesFromRate
          ? missingToTarget > 0
            ? `Tu porcentaje configura ${formatMoney(breakdown.savingsTarget)}. Después de tus apartados, faltan ${formatMoney(missingToTarget)} para llegar a esa meta este mes.`
            : `Tus apartados ya cubren la meta porcentual de ${formatMoney(breakdown.savingsTarget)}.`
          : `Tus apartados superan la meta porcentual; por eso el presupuesto protege ${formatMoney(breakdown.savingsTarget)}.`}
      </p>
    </div>
  );
}

/** Convierte el presupuesto mensual en una decisión cotidiana y accionable. */
function SpendingSpaceCard({ month, data }: { month: string; data: BudgetView }) {
  const cardQ = useCard();
  const insightsQ = useInsights(month);
  const categoriesQ = useCategories();
  const available = Math.max(0, data.availableFree);
  const [year, rawMonth] = month.split('-').map(Number);
  const today = new Date();
  const isCurrentMonth = year === today.getFullYear() && rawMonth === today.getMonth() + 1;
  const daysRemaining = isCurrentMonth
    ? Math.max(1, new Date(year, rawMonth, 0).getDate() - today.getDate() + 1)
    : null;
  const weeklyAmount = daysRemaining ? available / Math.max(1, daysRemaining / 7) : null;
  const policy = cardQ.data ? getPurchasePolicy(cardQ.data, data) : undefined;
  const categoryRows = useMemo(() => {
    const categories = new Map((categoriesQ.data ?? []).map((category) => [category.id, category]));
    const spent = new Map(
      (insightsQ.data?.spendingByCategory ?? []).map((item) => [item.categoryId ?? '', item.total]),
    );
    return Object.entries(data.byCategory)
      .filter(([, limit]) => limit > 0)
      .map(([categoryId, limit]) => {
        const category = categories.get(categoryId);
        const used = spent.get(categoryId) ?? 0;
        return {
          id: categoryId,
          label: category?.name ?? 'Categoría',
          icon: category?.icon,
          limit,
          used,
          remaining: limit - used,
        };
      })
      .sort((a, b) => a.remaining - b.remaining);
  }, [categoriesQ.data, data.byCategory, insightsQ.data?.spendingByCategory]);

  return (
    <section className="overflow-hidden rounded-[18px] border border-blue-100 bg-[linear-gradient(120deg,#eff6ff_0%,#fff_62%,#ecfdf5_100%)] p-4 shadow-[0_12px_28px_-20px_rgba(37,99,235,.32)] sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[.08em] text-savings">
            Control del límite mensual
          </p>
          <h2 className="mt-1 text-lg font-bold tracking-tight text-slate-900">
            {isCurrentMonth
              ? '¿Cuánto queda de mi límite de gasto?'
              : 'Estado del límite del mes seleccionado'}
          </h2>
          <p className="mt-1 max-w-xl text-sm leading-5 text-slate-600">
            {isCurrentMonth
              ? 'Es tu límite mensual menos el gasto variable registrado. No es el saldo de tus cuentas, el cierre estimado ni dinero extra por tener crédito.'
              : 'Muestra el límite de gasto de ese mes menos el gasto variable registrado; no representa el saldo de tus cuentas ni el cierre estimado.'}
          </p>
        </div>
        {policy && (
          <Link to="/tarjeta" className="button button-secondary">
            Simular con tarjeta
          </Link>
        )}
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <SpendingMetric
          label={
            data.availableFree < 0
              ? 'Exceso sobre el límite este mes'
              : 'Restante del límite este mes'
          }
          value={formatMoney(data.availableFree < 0 ? Math.abs(data.availableFree) : available)}
          detail={
            data.availableFree < 0
              ? `Vas ${formatMoney(Math.abs(data.availableFree))} por encima del límite.`
              : 'Para todos tus gastos variables.'
          }
          tone={data.availableFree < 0 ? 'risk' : 'good'}
        />
        <SpendingMetric
          label={daysRemaining ? 'Parte del restante por semana' : 'Cantidad prudente del límite'}
          value={formatMoney(
            daysRemaining ? (weeklyAmount ?? 0) : safeExtraSavings(data.availableFree),
          )}
          detail={
            daysRemaining
              ? `Distribuido en los ${daysRemaining} días que quedan.`
              : 'Para no consumir todo lo que queda del límite de una vez.'
          }
        />
        <SpendingMetric
          label={policy ? 'Tope de contado con tarjeta' : 'Colchón reservado'}
          value={formatMoney(policy?.cashCap ?? safeExtraSavings(data.availableFree))}
          detail={
            policy
              ? `Nueva MSI: hasta ${formatMoney(policy.monthlyInstallmentCap)}/mes.`
              : 'Configura tu tarjeta para calcular compras nuevas.'
          }
        />
      </div>

      {categoryRows.length > 0 && (
        <div className="mt-4 border-t border-blue-100 pt-3">
          <div className="mb-2 flex items-baseline justify-between gap-3">
            <h3 className="text-sm font-semibold text-slate-800">
              Restante del límite por categoría
            </h3>
            <span className="text-xs text-slate-600">Sólo categorías con límite configurado</span>
          </div>
          <div className="space-y-2">
            {categoryRows.map((row) => {
              const percentage =
                row.limit > 0 ? Math.min(100, Math.max(0, (row.used / row.limit) * 100)) : 0;
              const isOver = row.remaining < 0;
              return (
                <div
                  key={row.id}
                  className="rounded-xl border border-white/90 bg-white/80 px-3 py-2.5"
                >
                  <div className="flex justify-between gap-3 text-sm">
                    <span className="min-w-0 truncate font-medium text-slate-700">
                      {row.icon ? `${row.icon} ` : ''}
                      {row.label}
                    </span>
                    <span
                      className={`shrink-0 font-semibold tabular-nums ${isOver ? 'text-expense' : 'text-slate-900'}`}
                    >
                      {formatMoney(Math.max(0, row.remaining))}
                    </span>
                  </div>
                  <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className={`h-full rounded-full ${isOver ? 'bg-expense' : percentage >= 85 ? 'bg-warning' : 'bg-savings'}`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <HelperText className="mt-3" tone="info">
        Si destinas parte del límite a una compra o una meta, disminuye lo que queda para la otra
        decisión. El control de tarjeta usa la misma reserva de seguridad.
      </HelperText>
    </section>
  );
}

function SpendingMetric({
  label,
  value,
  detail,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  detail: string;
  tone?: 'good' | 'risk' | 'neutral';
}) {
  return (
    <div className="rounded-xl border border-white/90 bg-white/85 p-3.5 shadow-sm">
      <p className="text-[11px] font-bold uppercase tracking-[.07em] text-slate-600">{label}</p>
      <p
        className={`mt-1 text-xl font-bold tracking-tight tabular-nums ${tone === 'good' ? 'text-income' : tone === 'risk' ? 'text-expense' : 'text-slate-900'}`}
      >
        {value}
      </p>
      <p className="mt-1 text-xs leading-4 text-slate-600">{detail}</p>
    </div>
  );
}

function Row({
  label,
  value,
  positive,
  strong,
}: {
  label: string;
  value: number;
  positive?: boolean;
  strong?: boolean;
}) {
  return (
    <div className="flex items-center justify-between">
      <dt className={`text-slate-600 ${strong ? 'font-semibold text-slate-800' : ''}`}>{label}</dt>
      <dd
        className={`tabular-nums ${strong ? 'font-bold text-slate-900' : ''} ${
          positive ? 'text-income' : value < 0 ? 'text-expense' : 'text-slate-700'
        }`}
      >
        {formatMoney(value)}
      </dd>
    </div>
  );
}

function LimitForm({ month, data }: { month: string; data: BudgetView }) {
  const upsert = useUpsertBudget(month);
  const [manual, setManual] = useState(data.monthlyLimit != null ? String(data.monthlyLimit) : '');

  useEffect(() => {
    setManual(data.monthlyLimit != null ? String(data.monthlyLimit) : '');
  }, [data.monthlyLimit, month]);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    upsert.mutate({ monthlyLimit: manual === '' ? null : Number(manual) });
  };

  return (
    <section className="ui-card">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-slate-700">Tu límite de gasto variable</h2>
          <p className="mt-1 text-xs leading-5 text-slate-500">
            Es lo que permites para compras y gastos no fijos; no cambia tu ahorro objetivo ni los
            pagos comprometidos.
          </p>
        </div>
        <span
          className={`rounded-full px-2 py-1 text-xs font-semibold ${data.monthlyLimit === null ? 'bg-emerald-50 text-income' : 'bg-blue-50 text-savings'}`}
        >
          {data.monthlyLimit === null ? 'Usando sugerido' : 'Límite manual'}
        </span>
      </div>
      <form onSubmit={onSubmit} className="flex flex-wrap items-end gap-3">
        <label className="flex-1">
          <span className="mb-1 block text-xs text-slate-500">
            {data.monthlyLimit === null
              ? `Sugerido: ${formatMoney(data.suggestedLimit)}`
              : `Manual actual · sugerido: ${formatMoney(data.suggestedLimit)}`}
          </span>
          <input
            type="number"
            inputMode="decimal"
            step="0.01"
            placeholder={String(data.suggestedLimit)}
            value={manual}
            onChange={(e) => setManual(e.target.value)}
            className="tap-target w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-base"
          />
        </label>
        <button
          type="submit"
          disabled={upsert.isPending}
          className="tap-target rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60"
        >
          {upsert.isPending ? 'Guardando…' : 'Guardar'}
        </button>
        {data.monthlyLimit !== null && (
          <button
            type="button"
            disabled={upsert.isPending}
            onClick={() => {
              setManual('');
              upsert.mutate({ monthlyLimit: null });
            }}
            className="tap-target rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-semibold text-savings disabled:opacity-60"
          >
            Usar sugerido
          </button>
        )}
      </form>
      <HelperText className="mt-3" tone="info">
        Deja el campo vacío y guarda para volver al límite sugerido. Ajustarlo sólo cambia este mes.
      </HelperText>
      {upsert.isError && (
        <p role="alert" className="mt-2 text-sm text-expense">
          {getApiErrorMessage(upsert.error)}
        </p>
      )}
    </section>
  );
}

/**
 * Divide el límite global entre rubros sin convertir esos rubros en dinero
 * adicional. Está separado del formulario de límite mensual para que primero
 * sea claro cuánto se puede gastar en total y después cómo se reparte.
 */
function CategoryLimitsForm({ month, data }: { month: string; data: BudgetView }) {
  const categoriesQ = useCategories();
  const upsert = useUpsertBudget(month);
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [savedNotice, setSavedNotice] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const expenseCategories = useMemo(
    () =>
      (categoriesQ.data ?? [])
        .filter((category) => category.kind === CategoryKind.EXPENSE)
        .sort((a, b) => a.name.localeCompare(b.name, 'es')),
    [categoriesQ.data],
  );

  useEffect(() => {
    if (isDirty) return;
    setDraft(
      Object.fromEntries(
        expenseCategories.map((category) => [
          category.id,
          data.byCategory[category.id] > 0 ? String(data.byCategory[category.id]) : '',
        ]),
      ),
    );
  }, [data.byCategory, expenseCategories, isDirty, month]);

  useEffect(() => {
    setSavedNotice(false);
    setIsDirty(false);
  }, [month]);

  const limits = toCategoryLimitPayload(
    draft,
    expenseCategories.map((category) => category.id),
  );
  const hasUnsavedChanges = expenseCategories.some(
    (category) => (limits[category.id] ?? 0) !== (data.byCategory[category.id] ?? 0),
  );
  const allocation = categoryLimitAllocation(limits, data.effectiveLimit);
  const configuredCount = Object.keys(limits).length;
  const hasInvalidAmount = Object.values(draft).some((raw) => {
    if (raw.trim() === '') return false;
    const amount = Number(raw);
    return !Number.isFinite(amount) || amount < 0;
  });
  const validationMessage = hasInvalidAmount
    ? 'Cada tope debe ser un monto igual o mayor a cero.'
    : allocation.isOverAllocated
      ? `Los topes exceden tu límite general por ${formatMoney(Math.abs(allocation.remaining))}. Reduce alguno o ajusta primero el límite mensual.`
      : null;

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (validationMessage) return;
    upsert.mutate(
      { byCategory: limits },
      {
        onSuccess: () => {
          setSavedNotice(true);
          setIsDirty(false);
        },
      },
    );
  };

  const updateDraft = (categoryId: string, value: string) => {
    if (!upsert.isPending) upsert.reset();
    setSavedNotice(false);
    setIsDirty(true);
    setDraft((current) => ({ ...current, [categoryId]: value }));
  };

  return (
    <section className="ui-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-sm font-semibold text-slate-700">Topes por categoría</h2>
            {hasUnsavedChanges && (
              <span
                role="status"
                className="inline-flex items-center rounded-full bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-800"
              >
                Topes modificados · Guarda los cambios
              </span>
            )}
          </div>
          <p className="mt-1 max-w-xl text-xs leading-5 text-slate-500">
            Reparte tu límite de gasto variable entre rubros. Los topes no aumentan tu límite
            general.
          </p>
          <p className="mt-3 max-w-xl rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-900">
            <strong>Úsalos sólo para gasto variable.</strong> Los cargos fijos ya se descuentan
            antes de calcular este límite; asignarlos aquí alteraría el cálculo disponible para el
            resto de tus gastos.
          </p>
        </div>
        <button
          type="button"
          className="button button-secondary"
          aria-expanded={expanded}
          aria-controls="category-limit-fields"
          onClick={() => setExpanded((isExpanded) => !isExpanded)}
        >
          {expanded ? 'Ocultar' : configuredCount > 0 ? 'Ajustar topes' : 'Configurar topes'}
        </button>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2" aria-label="Resumen de distribución del límite">
        <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5">
          <p className="text-[11px] font-bold uppercase tracking-[.07em] text-slate-500">
            Asignado
          </p>
          <p className="mt-0.5 font-semibold tabular-nums text-slate-900">
            {formatMoney(allocation.allocated)}
          </p>
          <p className="mt-0.5 text-xs text-slate-500">
            {configuredCount} {configuredCount === 1 ? 'categoría' : 'categorías'}
          </p>
        </div>
        <div
          className={`rounded-xl border px-3 py-2.5 ${allocation.isOverAllocated ? 'border-red-100 bg-red-50' : 'border-emerald-100 bg-emerald-50/70'}`}
        >
          <p
            className={`text-[11px] font-bold uppercase tracking-[.07em] ${allocation.isOverAllocated ? 'text-expense' : 'text-slate-600'}`}
          >
            {allocation.isOverAllocated ? 'Por corregir' : 'Por asignar'}
          </p>
          <p
            className={`mt-0.5 font-semibold tabular-nums ${allocation.isOverAllocated ? 'text-expense' : 'text-income'}`}
          >
            {formatMoney(Math.abs(allocation.remaining))}
          </p>
          <p className="mt-0.5 text-xs text-slate-600">
            de {formatMoney(data.effectiveLimit)} disponibles en total
          </p>
        </div>
      </div>

      {expanded && (
        <form
          id="category-limit-fields"
          onSubmit={onSubmit}
          className="mt-4 border-t border-slate-100 pt-4"
        >
          {categoriesQ.isLoading ? (
            <SkeletonState label="Cargando categorías…" className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </SkeletonState>
          ) : categoriesQ.isError ? (
            <div role="alert" className="flex flex-wrap items-center gap-2 text-sm text-expense">
              <span>No pudimos cargar las categorías: {getApiErrorMessage(categoriesQ.error)}</span>
              <button
                type="button"
                className="tap-target -my-2 px-2 font-semibold text-savings hover:text-blue-700"
                onClick={() => void categoriesQ.refetch()}
              >
                Reintentar
              </button>
            </div>
          ) : expenseCategories.length === 0 ? (
            <p className="text-sm leading-5 text-slate-600">
              Aún no tienes categorías de gasto.{' '}
              <Link
                to="/ajustes#categorías"
                className="tap-target inline-flex items-center font-semibold text-savings hover:text-blue-700"
              >
                Créala en Ajustes →
              </Link>
            </p>
          ) : (
            <>
              <p className="mb-3 text-xs leading-5 text-slate-500">
                Deja un campo vacío para no poner tope a ese rubro. Sólo aparecen categorías de
                gasto.
              </p>
              <div className="grid gap-2 sm:grid-cols-2">
                {expenseCategories.map((category) => {
                  const rawValue = draft[category.id] ?? '';
                  const inputId = `category-limit-${category.id}`;
                  return (
                    <div
                      key={category.id}
                      className="rounded-xl border border-slate-200 bg-white px-3 py-2.5"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <label
                          htmlFor={inputId}
                          className="min-w-0 truncate text-sm font-medium text-slate-700"
                        >
                          {category.icon ? `${category.icon} ` : ''}
                          {category.name}
                        </label>
                        {rawValue !== '' && (
                          <button
                            type="button"
                            disabled={upsert.isPending}
                            onClick={() => updateDraft(category.id, '')}
                            className="tap-target -my-2 -mr-2 shrink-0 px-2 text-xs font-semibold text-slate-500 hover:text-slate-800 disabled:opacity-50"
                            aria-label={`Quitar límite de ${category.name}`}
                          >
                            Sin límite
                          </button>
                        )}
                      </div>
                      <div className="mt-2 flex items-center rounded-lg border border-slate-200 bg-slate-50 px-2.5 transition-colors focus-within:border-slate-300 focus-within:bg-white focus-within:shadow-[0_0_0_3px_rgba(15,23,42,.08)]">
                        <span aria-hidden className="mr-1 text-sm font-semibold text-slate-600">
                          $
                        </span>
                        <input
                          id={inputId}
                          type="number"
                          inputMode="decimal"
                          min="0"
                          step="0.01"
                          placeholder="Sin límite"
                          value={rawValue}
                          disabled={upsert.isPending}
                          className="category-limit-input min-h-[44px] w-full bg-transparent py-1 text-base tabular-nums text-slate-900 outline-none placeholder:text-slate-600 disabled:cursor-not-allowed disabled:text-slate-500"
                          onChange={(event) => updateDraft(category.id, event.target.value)}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              {validationMessage && (
                <p
                  role="alert"
                  className="mt-3 rounded-xl bg-red-50 px-3 py-2 text-sm leading-5 text-expense"
                >
                  {validationMessage}
                </p>
              )}
              {upsert.isError && (
                <p role="alert" className="mt-3 text-sm text-expense">
                  {getApiErrorMessage(upsert.error)}
                </p>
              )}
              {savedNotice && !validationMessage && (
                <p role="status" className="mt-3 text-sm font-medium text-income">
                  Topes guardados para este mes.
                </p>
              )}
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                <p className="text-xs leading-5 text-slate-500">
                  Estos topes aplican sólo a {month}.
                </p>
                <button
                  type="submit"
                  disabled={upsert.isPending || Boolean(validationMessage)}
                  className="button button-primary"
                >
                  {upsert.isPending ? 'Guardando…' : 'Guardar topes'}
                </button>
              </div>
            </>
          )}
        </form>
      )}
    </section>
  );
}
